"use client"

import { validateLanguageCode } from "@/data/supported-languages"
import logger from "@/lib/logger/logger-init"
import { type RequiredTokenAndSelectionMetadata } from "@/types/cookie-types"
import { type Result, ResultFactory } from "@/types/generic/Result"
import { type ChangeShippingDestinationOrLanguageResult } from "./types"

export interface LanguageCodeFormData extends FormData {
    append(name: "languageCode", value: string | Blob, fileName?: string): void
}

export const changeLanguage = async (
    formData: LanguageCodeFormData
): Promise<Result<ChangeShippingDestinationOrLanguageResult>> => {
    const languageCode = formData.get("languageCode")

    if (!languageCode || typeof languageCode !== "string") {
        const errorMessage = "Language code is required"
        logger.error(errorMessage)
        return ResultFactory.failure<ChangeShippingDestinationOrLanguageResult>(
            new Error(errorMessage)
        )
    }

    const validation = validateLanguageCode(languageCode)
    if (!validation.success) {
        const errorMessage = "Invalid language code"
        logger.error(errorMessage, { languageCode })
        return ResultFactory.failure<ChangeShippingDestinationOrLanguageResult>(
            new Error(errorMessage)
        )
    }

    try {
        const response = await fetch("/api/centra/change-language", {
            method: "POST",
            credentials: "include",
            body: formData
        })

        const result = await response.json()

        if (!response.ok || !result.success) {
            const errorMessage = result.error || "Failed to change language"
            logger.error(errorMessage)
            return ResultFactory.failure<ChangeShippingDestinationOrLanguageResult>(
                new Error(errorMessage)
            )
        }

        const { checkoutApiSelection, basketSessionCookie } = result.data

        // Set-Cookie from the route is enough.

        return ResultFactory.success<ChangeShippingDestinationOrLanguageResult>({
            checkoutApiSelection: checkoutApiSelection,
            basketSessionCookie: basketSessionCookie as RequiredTokenAndSelectionMetadata
        })
    } catch (error) {
        logger.error("Error in changeLanguage client action", error)
        return ResultFactory.failure<ChangeShippingDestinationOrLanguageResult>(
            error instanceof Error ? error : new Error("Failed to change language")
        )
    }
}
